import { Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import ConversionFactorCustomService from '../../../shared/services/conversion-factor-custom/conversion-factor-custom-service';
import type { IPostConversionFactorCustom } from '../../../shared/services/conversion-factor-custom/conversion-factor-custom.model';
import ConversionService from '../../../shared/services/conversion/conversion.service';
import { SECR_REPORTING_SOURCE_OPTIONS } from '../../../shared/constants/secr-reporting-sources';

const STATUS_OPTIONS: { value: 'unmapped' | 'suggested' | 'confirmed'; label: string }[] = [
  { value: 'unmapped', label: 'Unmapped' },
  { value: 'suggested', label: 'Suggested (use system suggestion)' },
  { value: 'confirmed', label: 'Confirmed (use reporting source below)' },
];

export const SuperAdminSecrMappingCard: FunctionComponent<{
  /** Gaia universal custom factor (bulk-updates all year rows) */
  factorId?: string;
  /** Single conversion row (e.g. DEFRA library) */
  conversionId?: string;
  suggestedKey: string | null | undefined;
  secrSourceKey: string | null | undefined;
  status: 'unmapped' | 'suggested' | 'confirmed' | null | undefined;
  onSaved: () => void;
}> = ({ factorId, conversionId, suggestedKey, secrSourceKey, status, onSaved }) => {
  const userNotificationsContext = useContext(UserNotificationsContext);
  const factorService = new ConversionFactorCustomService();
  const conversionService = new ConversionService();
  const [reportingKey, setReportingKey] = useState(secrSourceKey ?? '');
  const [mappingStatus, setMappingStatus] = useState<'unmapped' | 'suggested' | 'confirmed'>(
    status === 'unmapped' || status === 'suggested' || status === 'confirmed' ? status : 'suggested'
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setReportingKey(secrSourceKey ?? '');
    setMappingStatus(
      status === 'unmapped' || status === 'suggested' || status === 'confirmed' ? status : 'suggested'
    );
  }, [secrSourceKey, status, factorId, conversionId]);

  const applySuggested = () => {
    if (suggestedKey) {
      setReportingKey(suggestedKey);
      setMappingStatus('confirmed');
    }
  };

  const save = () => {
    if (mappingStatus === 'confirmed' && !reportingKey) {
      return;
    }
    setSaving(true);
    const patchBody: {
      secrSourceMappingStatus: 'unmapped' | 'suggested' | 'confirmed';
      secrSourceKey?: string | null;
    } = { secrSourceMappingStatus: mappingStatus };
    if (mappingStatus === 'confirmed') {
      patchBody.secrSourceKey = reportingKey;
    } else if (mappingStatus === 'unmapped') {
      patchBody.secrSourceKey = null;
    }
    const savePromise =
      conversionId != null
        ? conversionService.patchSecrMapping(conversionId, patchBody)
        : factorId != null
          ? factorService.edit(factorId, patchBody as Partial<IPostConversionFactorCustom>)
          : Promise.reject(new Error('Missing factorId or conversionId'));
    savePromise
      .then(() => {
        onSaved();
        userNotificationsContext?.openSnackbar({ open: true, message: 'SECR mapping saved' });
      })
      .catch(() => {
        userNotificationsContext?.openSnackbar({
          open: true,
          message: 'Could not save SECR mapping',
          backgroundColor: 'text.primary',
        });
      })
      .finally(() => setSaving(false));
  };

  return (
    <Box sx={{ mt: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, maxWidth: 560 }}>
      <Typography variant='h6' sx={{ mb: 1.5 }}>
        SECR reporting source
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Reporting uses the conversion factor mapping. Confirm a source for compliance outputs; unmapped values roll up to &quot;Other / not yet classified&quot;.
      </Typography>
      <Stack spacing={2}>
        <TextField
          label='System suggestion'
          value={suggestedKey ?? ''}
          fullWidth
          size='small'
          InputProps={{ readOnly: true }}
          helperText={
            conversionId != null
              ? 'Updated when conversion details or category change'
              : 'Updated when factor details or category change'
          }
        />
        <TextField
          select
          label='Mapping status'
          value={mappingStatus}
          size='small'
          fullWidth
          onChange={(e) => setMappingStatus(e.target.value as 'unmapped' | 'suggested' | 'confirmed')}
        >
          {STATUS_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label='Reporting source (when confirmed)'
          value={reportingKey}
          size='small'
          fullWidth
          disabled={mappingStatus !== 'confirmed'}
          onChange={(e) => setReportingKey(e.target.value)}
          helperText={mappingStatus === 'confirmed' ? 'Required for confirmed' : ' '}
        >
          <MenuItem value=''>
            <em>Select…</em>
          </MenuItem>
          {SECR_REPORTING_SOURCE_OPTIONS.map((o) => (
            <MenuItem key={o.key} value={o.key}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
          <Button variant='outlined' size='small' onClick={applySuggested} disabled={!suggestedKey}>
            Use suggested source
          </Button>
          <Button variant='contained' size='small' onClick={save} disabled={saving}>
            Save SECR mapping
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
